import React from 'react';
import {
  Box,
  Container,
  Flex,
  Heading,
  HStack,
  Icon,
  Image,
  Link,
  Tag,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import { FaExternalLinkAlt, FaGithub } from 'react-icons/fa';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);

const fadeInUpVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.6, -0.05, 0.01, 0.99] }
  }
};

const staggerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2 }
  }
};

const Project = ({ title, description, technologies, githubLink, liveLink, imageSrc }) => {
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const tagBg = useColorModeValue('gray.100', 'gray.700');
  const iconColor = useColorModeValue('gray.600', 'gray.400');
  const liveLinkColor = useColorModeValue('black', 'white');
  const gradientText = useColorModeValue(
    'linear(to-r, #200122, #6f0000)',
    'linear(to-r, #bdc3c7, white)'
  );

  return (
    <MotionBox
      py={8}
      borderBottom="1px"
      borderColor={borderColor}
      variants={fadeInUpVariants}
    >
      <Flex direction={{ base: 'column', md: 'row' }} justify="space-between" align="center">
        <Box flex="1" pr={{ base: 0, md: 6 }} mb={{ base: 6, md: 0 }}>
          <Heading
            fontSize="2xl"
            mb={3}
            fontWeight="semibold"
            bgGradient={gradientText}
            bgClip="text"
          >
            {title}
          </Heading>
          <Text fontSize="md" mb={4} color={useColorModeValue('gray.600', 'gray.400')}>{description}</Text>
          <HStack spacing={2} mb={4} flexWrap="wrap">
            {technologies.map((tech, index) => (
              <Tag key={index} size="sm" bg={tagBg} my={1}>
                {tech}
              </Tag>
            ))}
          </HStack>
          <HStack spacing={4}>
            {githubLink && (
              <Link href={githubLink} isExternal color={iconColor} fontSize="sm">
                <Icon as={FaGithub} mr={1} />
                GitHub
              </Link>
            )}
            {liveLink && (
              <Link href={liveLink} isExternal color={liveLinkColor} fontWeight="medium" fontSize="sm" >
                <Icon as={FaExternalLinkAlt} mr={1} />
                Visita
              </Link>
            )}
          </HStack>
        </Box>
        <Box
          width={{ base: 'full', md: '200px' }}
          height={{ base: '200px', md: '150px' }}
          flexShrink={0}
        >
          <Image
            src={imageSrc}
            alt={title}
            objectFit="cover"
            width="100%"
            height="100%"
            borderRadius="md"
            fallback={<Box bg={useColorModeValue('gray.100', 'gray.700')} width="100%" height="100%" borderRadius="md" />}
          />
        </Box>
      </Flex>
    </MotionBox>
  );
};

export default function FeaturedProjects() {
  const gradientHeading = useColorModeValue(
    'linear(to-r, #200122, #6f0000)',
    'linear(to-r, #bdc3c7, white)'
  );

  return (
    <Box py={24} id="projects">
      <Container maxW="container.md">
        <Heading
          as="h2"
          size="2xl"
          pb={2}
          mb={2}
          fontWeight="bold"
          bgGradient={gradientHeading}
          color={gradientHeading}
          bgClip="text"
        >
          Progetti
        </Heading>
        <VStack spacing={0} align="stretch">
          <Project
            title="Charlie Roleplay"
            description="Server Minecraft Roleplay ('gioco di ruolo') con l'utilizzo di API da 30+ plugin personalizzati."
            technologies={['Java', 'HikariCP', 'Gradle', 'Paper API']}
            liveLink="https://charlieroleplay.it"
            imageSrc="/charlierp.png"
          />
          <Project
            title="AnotherLevel"
            description="Innovazione nelle piccole cose, esplorando nuove frontiere tecnologiche."
            technologies={['ReactJS', 'ChakraUI']}
            liveLink="https://anotherlevel.it"
            imageSrc="/anotherlevel.png"
          />
        </VStack>
      </Container>
    </Box>
  );
}
